import { ArrowUpRight, TrendingUp, Users, Mail, Zap } from "lucide-react";
import { useInView } from "../hooks/useInView";
import WaitlistForm from "./WaitlistForm";

const metrics = [
  { label: "Active Contacts", value: "2,847", change: "+12.4%", icon: Users },
  { label: "Emails Drafted", value: "342", change: "+28%", icon: Mail },
  { label: "Warmth Score", value: "87.3", change: "+5.1%", icon: TrendingUp },
  { label: "Intro Paths", value: "156", change: "+18", icon: Zap },
];

export default function HeroSection() {
  const [sectionRef, isInView] = useInView();

  return (
    <section
      id="hero"
      data-testid="hero-section"
      ref={sectionRef}
      className="min-h-[85vh] flex flex-col justify-center pl-8 md:pl-16 pr-4 md:pr-8 py-12 md:py-24 relative"
    >
      {/* Top label */}
      <div
        className={`mb-6 transition-all duration-700 ${
          isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
        }`}
      >
        <span className="text-xs uppercase tracking-[0.15em] font-semibold text-[#a33900]">
          Relationship Intelligence Platform
        </span>
      </div>

      {/* Main content grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-start">
        {/* Left column - Headline */}
        <div
          className={`lg:col-span-7 transition-all duration-700 delay-100 ${
            isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-[#1c1b1d] leading-[1.05]">
            Your network is
            <br />
            your{" "}
            <span className="bg-gradient-to-r from-[#a33900] to-[#cc4900] bg-clip-text text-transparent">
              net worth.
            </span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-[#5a4138] leading-relaxed max-w-xl">
            Relio quietly analyzes your Gmail history to surface relationship decay,
            draft perfect follow-ups with Claude AI, and map the fastest intro paths
            through your existing network.
          </p>

          {/* CTAs */}
          <div className="mt-8 flex flex-wrap gap-4">
            <a
              href="https://app.relio.in"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-button"
              className="cta-gradient text-white rounded-xl px-7 py-3.5 font-medium shadow-[0_8px_20px_rgba(204,73,0,0.25)] transition-transform hover:-translate-y-0.5 inline-flex items-center gap-2"
            >
              Join Now
              <ArrowUpRight size={16} />
            </a>
            <button
              data-testid="hero-secondary-cta"
              onClick={() => document.getElementById("ai-demo")?.scrollIntoView({ behavior: "smooth" })}
              className="bg-[#f0edf0] hover:bg-[#e2bfb2]/30 text-[#1c1b1d] rounded-xl px-7 py-3.5 font-medium transition-colors"
            >
              Try AI Demo
            </button>
          </div>

          {/* Waitlist */}
          <div className="mt-8 max-w-md">
            <p className="text-xs text-[#5a4138] mb-2 font-medium">Get early access updates</p>
            <WaitlistForm variant="inline" />
          </div>
        </div>

        {/* Right column - Floating metric cards */}
        <div
          className={`lg:col-span-5 transition-all duration-700 delay-200 ${
            isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          <div className="glass rounded-3xl p-6 shadow-[0_16px_40px_rgba(163,57,0,0.08)]">
            <div className="flex items-center justify-between mb-5">
              <span className="text-xs uppercase tracking-[0.1em] font-semibold text-[#5a4138]">
                Network Overview
              </span>
              <span className="text-[10px] text-[#a33900] font-medium bg-[#ff9971]/20 px-2 py-0.5 rounded-full">
                Live
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {metrics.map((m) => {
                const Icon = m.icon;
                return (
                  <div
                    key={m.label}
                    data-testid={`metric-${m.label.toLowerCase().replace(/\s/g, '-')}`}
                    className="bg-[#fcf8fb] rounded-2xl p-4 group hover:bg-[#f6f2f5] transition-colors"
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <Icon size={14} className="text-[#a33900]" strokeWidth={1.5} />
                      <span className="text-[10px] uppercase tracking-wider font-semibold text-[#5a4138]">
                        {m.label}
                      </span>
                    </div>
                    <p className="text-2xl font-semibold text-[#1c1b1d] tracking-tight">
                      {m.value}
                    </p>
                    <span className="text-xs text-green-600 font-medium mt-1 inline-block">
                      {m.change}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Mini pipeline card */}
          <div className="glass rounded-2xl p-5 mt-4 shadow-[0_16px_40px_rgba(163,57,0,0.06)]">
            <span className="text-[10px] uppercase tracking-wider font-semibold text-[#5a4138]">
              Pipeline Activity
            </span>
            <div className="mt-3 space-y-2.5">
              {[
                { name: "Series A — ACME Corp", stage: "Term Sheet", color: "bg-green-500" },
                { name: "Angel — Sarah Chen", stage: "Due Diligence", color: "bg-[#cc4900]" },
                { name: "Seed — Vertex Partners", stage: "First Meeting", color: "bg-[#ff9971]" },
              ].map((deal) => (
                <div key={deal.name} className="flex items-center gap-3">
                  <div className={`w-1.5 h-1.5 rounded-full ${deal.color}`} />
                  <span className="text-sm text-[#1c1b1d] font-medium flex-1 truncate">{deal.name}</span>
                  <span className="text-[10px] text-[#5a4138] bg-[#f0edf0] px-2 py-0.5 rounded-full">{deal.stage}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
